import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import {
  AppOfferBanner as AppOfferBannerModel,
  bannerActionsClusterClass,
  bannerContentClusterClass,
  bannerMessageTextClass,
  bannerOuterLayoutClass,
  clampDismissHideDays,
  DEFAULT_DISMISS_HIDE_DAYS,
  getCtaButtonPresentation,
  getEligibleBannerForPath,
  logBannerEvent,
  normalizeContentAlign,
  normalizeCtaButtonRadius,
  normalizeCtaButtonVariant,
  normalizeShowBadge,
  normalizeShowCta,
  safeBannerBackgroundImageUrl,
} from '../services/appOfferBannerService';

const IMPRESSION_KEY = (id: string) => `app_offer_banner_impression_${id}`;
const DISMISS_KEY = (id: string) => `app_offer_banner_dismissed_${id}`;
const PROMO_TOKEN = '{{PROMO}}';

const MS_PER_DAY = 86400000;

/** Legacy: permanent hide. New: JSON `{ until: epochMs }`. */
function isDismissedInStorage(bannerId: string): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(DISMISS_KEY(bannerId));
  if (raw == null || raw === '') return false;
  if (raw === '1') return true;
  try {
    const o = JSON.parse(raw) as { until?: unknown };
    if (typeof o.until === 'number' && Number.isFinite(o.until)) {
      if (Date.now() < o.until) return true;
      window.localStorage.removeItem(DISMISS_KEY(bannerId));
      return false;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function PromoChip({ code, textColor }: { code: string; textColor: string }) {
  return (
    <span
      className="inline-flex items-center mx-0.5 px-1.5 py-0.5 rounded bg-black/30 font-semibold text-xs sm:text-sm align-middle border border-white/10"
      style={{ color: textColor }}
      translate="no"
    >
      {code}
    </span>
  );
}

function renderMessageBody(message: string, promoCode: string, messageTextColor: string): React.ReactNode {
  const code = promoCode.trim();
  if (code && message.includes(PROMO_TOKEN)) {
    const parts = message.split(PROMO_TOKEN);
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {part}
            {i < parts.length - 1 ? <PromoChip code={code} textColor={messageTextColor} /> : null}
          </React.Fragment>
        ))}
      </>
    );
  }
  if (code) {
    return (
      <>
        {message}
        {message.trim().length > 0 ? ' ' : null}
        <PromoChip code={code} textColor={messageTextColor} />
      </>
    );
  }
  return message;
}

const AppOfferBanner: React.FC = () => {
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const { openUpgradeModal } = useUpgradeModal();
  const [banner, setBanner] = useState<AppOfferBannerModel | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await getEligibleBannerForPath(pathname);
        if (cancelled) return;
        setBanner(b);
        if (b && typeof window !== 'undefined') {
          setDismissed(isDismissedInStorage(b.id));
        } else {
          setDismissed(false);
        }
      } catch (e) {
        console.error('AppOfferBanner: failed to load', e);
        if (!cancelled) setBanner(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // Snooze can expire while the tab stays open — re-check when user returns to the tab.
  useEffect(() => {
    if (!banner || typeof window === 'undefined') return;
    const syncDismissed = () => setDismissed(isDismissedInStorage(banner.id));
    const onVisibility = () => {
      if (document.visibilityState === 'visible') syncDismissed();
    };
    window.addEventListener('focus', syncDismissed);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', syncDismissed);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [banner?.id]);

  useEffect(() => {
    if (!banner || !currentUser?.uid || dismissed) return;
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem(IMPRESSION_KEY(banner.id))) return;
    sessionStorage.setItem(IMPRESSION_KEY(banner.id), '1');
    logBannerEvent(banner.id, 'impression', pathname, currentUser.uid).catch((err) =>
      console.warn('AppOfferBanner: impression log failed', err)
    );
  }, [banner, pathname, currentUser?.uid, dismissed]);

  const handleCtaClick = useCallback(() => {
    if (!banner || !currentUser?.uid) return;
    const ctaClickKind =
      banner.ctaTarget === 'upgrade_modal'
        ? 'upgrade_modal'
        : banner.ctaStyle === 'link'
          ? 'link'
          : 'button';
    logBannerEvent(banner.id, 'cta_click', pathname, currentUser.uid, { ctaClickKind }).catch((err) =>
      console.warn('AppOfferBanner: cta log failed', err)
    );
  }, [banner, pathname, currentUser?.uid]);

  const handleUpgradeCtaClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      handleCtaClick();
      openUpgradeModal();
    },
    [handleCtaClick, openUpgradeModal]
  );

  const dismiss = useCallback(() => {
    if (!banner || !banner.dismissible) return;
    const days = clampDismissHideDays(banner.dismissHideDays ?? DEFAULT_DISMISS_HIDE_DAYS);
    const until = Date.now() + days * MS_PER_DAY;
    localStorage.setItem(DISMISS_KEY(banner.id), JSON.stringify({ until }));
    setDismissed(true);
    if (currentUser?.uid) {
      logBannerEvent(banner.id, 'dismiss', pathname, currentUser.uid).catch((err) =>
        console.warn('AppOfferBanner: dismiss log failed', err)
      );
    }
  }, [banner, pathname, currentUser?.uid]);

  const ctaIsExternal = useMemo(() => {
    if (!banner?.ctaUrl?.trim() || banner.ctaTarget === 'upgrade_modal') return false;
    const u = banner.ctaUrl.trim();
    return /^https?:\/\//i.test(u);
  }, [banner]);

  if (!banner || dismissed) return null;

  const align = normalizeContentAlign(banner.contentAlign);
  const showBadge = normalizeShowBadge(banner.showBadge);
  const showCta = normalizeShowCta(banner.showCta);
  const hasBadge = showBadge && Boolean(banner.badgeText?.trim());
  const hasCta = showCta && Boolean(banner.ctaLabel?.trim() && (banner.ctaTarget === 'upgrade_modal' || banner.ctaUrl?.trim()));
  const ctaIsLink = banner.ctaStyle === 'link';
  const ctaOpensUpgrade = banner.ctaTarget === 'upgrade_modal';

  const messageTextColor = (banner.messageTextColor || '').trim() || '#ffffff';
  const ctaTextColor = (banner.ctaTextColor || '').trim() || '#ffffff';
  const ctaButtonBorderColor = (banner.ctaButtonBorderColor || '').trim() || 'rgba(255,255,255,0.9)';
  const ctaButtonBgColor = (banner.ctaButtonBgColor || '').trim();

  const ctaLinkClass =
    'inline-flex items-center gap-1 text-xs sm:text-sm font-medium underline underline-offset-4 transition whitespace-nowrap';
  const ctaLinkStyle = {
    color: ctaTextColor,
    textDecorationColor: ctaTextColor,
  } as React.CSSProperties;

  const btnVariant = normalizeCtaButtonVariant(banner.ctaButtonVariant);
  const btnRadius = normalizeCtaButtonRadius(banner.ctaButtonRadius);
  const ctaBtnPres = !ctaIsLink
    ? getCtaButtonPresentation(btnVariant, btnRadius, {
        ctaTextColor,
        ctaButtonBorderColor,
        ctaButtonBgColor,
      })
    : null;

  const ctaInteractClass = ctaIsLink ? ctaLinkClass : (ctaBtnPres?.className ?? '');
  const ctaInteractStyle = (ctaIsLink ? ctaLinkStyle : (ctaBtnPres?.style ?? {})) as React.CSSProperties;

  const bgImg = safeBannerBackgroundImageUrl(banner.backgroundImageUrl);

  return (
    <div
      className="relative z-30 flex-shrink-0 overflow-hidden border-b border-white/10"
      role="region"
      aria-label="Announcement"
    >
      <div
        className="relative z-10 px-3 py-2 sm:px-4 sm:py-2.5"
        style={
          bgImg
            ? {
                backgroundImage: `url(${JSON.stringify(bgImg)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#0f2744',
              }
            : { background: banner.backgroundCss || '#0f2744' }
        }
      >
        <div className={`max-w-[1600px] mx-auto ${bannerOuterLayoutClass(align)}`}>
          <div className={bannerContentClusterClass(align)}>
            {hasBadge ? (
              <span
                className="inline-flex shrink-0 items-center self-center px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wide"
                style={{
                  backgroundColor: banner.badgeBgColor || '#22c55e',
                  color: banner.badgeTextColor || '#ffffff',
                }}
              >
                {banner.badgeText.trim()}
              </span>
            ) : null}
            <p
              className={`text-xs sm:text-sm leading-snug sm:leading-normal min-w-0 flex-1 ${bannerMessageTextClass(align)}`}
              style={{ color: messageTextColor }}
            >
              {renderMessageBody(banner.message || '', banner.promoCode || '', messageTextColor)}
            </p>
          </div>
          <div className={bannerActionsClusterClass(align)}>
            {banner.dismissible ? (
              <button
                type="button"
                onClick={dismiss}
                className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
                style={{ color: messageTextColor, opacity: 0.85 }}
                aria-label="Dismiss announcement"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            ) : null}
            {hasCta ? (
              ctaOpensUpgrade ? (
                <button
                  type="button"
                  onClick={handleUpgradeCtaClick}
                  className={ctaInteractClass}
                  style={ctaInteractStyle}
                >
                  {banner.ctaLabel.trim()}
                </button>
              ) : ctaIsExternal ? (
                <a
                  href={banner.ctaUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCtaClick}
                  className={ctaInteractClass}
                  style={ctaInteractStyle}
                >
                  {banner.ctaLabel.trim()}
                  <span aria-hidden>→</span>
                </a>
              ) : (
                <Link
                  to={banner.ctaUrl.trim().startsWith('/') ? banner.ctaUrl.trim() : `/${banner.ctaUrl.trim()}`}
                  onClick={handleCtaClick}
                  className={ctaInteractClass}
                  style={ctaInteractStyle}
                >
                  {banner.ctaLabel.trim()}
                  <span aria-hidden>→</span>
                </Link>
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppOfferBanner;
